import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { t } from '@/shared/lib/i18n'
import { Button } from '@/shared/ui/button'
import { Label } from '@/shared/ui/label'
import { Textarea } from '@/shared/ui/textarea'
import { reportsMonthlySignPath } from '@/shared/lib/routes'

interface Props {
  reportId: number
  onClose: () => void
}

const ReportRejectModal = ({ reportId, onClose }: Props) => {
  const { error: serverError } = usePage<{ error?: string }>().props
  const [comments, setComments] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleReject = () => {
    if (!comments.trim()) {
      setValidationError(t('reports.signatures.rejectReasonRequired'))
      return
    }
    setValidationError(null)
    setSubmitting(true)
    router.post(
      reportsMonthlySignPath({ id: reportId }),
      { is_signed: false, comments: comments.trim() },
      {
        onSuccess: () => onClose(),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  const displayedError = validationError ?? serverError ?? null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-lg p-6 w-full max-w-md space-y-4">
        <h3 className="text-lg font-semibold">{t('reports.signatures.reject')}</h3>
        <div className="space-y-2">
          <Label htmlFor={`reject-reason-${reportId}`}>
            {t('reports.signatures.rejectReason')}
            <span className="text-destructive ml-0.5">*</span>
          </Label>
          <Textarea
            id={`reject-reason-${reportId}`}
            placeholder={t('reports.signatures.rejectReasonPlaceholder')}
            value={comments}
            onChange={(e) => {
              setComments(e.target.value)
              if (validationError && e.target.value.trim()) setValidationError(null)
            }}
            rows={3}
            aria-describedby={displayedError ? `reject-error-${reportId}` : undefined}
            aria-invalid={displayedError != null}
          />
          {displayedError && (
            <p id={`reject-error-${reportId}`} className="text-sm text-destructive">
              {displayedError}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('reports.signatures.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleReject} disabled={submitting}>
            {t('reports.signatures.reject')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ReportRejectModal
